import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../App.css';

const socialLinks = [
  { name: 'Facebook', href: 'https://facebook.com', icon: 'facebook' },
  { name: 'Twitter', href: 'https://twitter.com', icon: 'twitter' },
  { name: 'LinkedIn', href: 'https://linkedin.com', icon: 'linkedin' },
  { name: 'Instagram', href: 'https://instagram.com', icon: 'instagram' }
];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EMPTY_PLANS = { lab: [], supplier: [], phlebotomist: [] };

function planSummaryLine(plan, role) {
  if (role === 'lab') {
    return `Clinics: ${plan.clinicsLimit ?? '—'} · Jobs/mo: ${plan.jobPostingLimit ?? '—'}`;
  }
  if (role === 'supplier') {
    return `Offers/mo: ${plan.offerLimit ?? '—'}`;
  }
  const cap = plan.biddingLimit ?? 0;
  return `Bids: ${cap >= 900 ? 'Unlimited' : cap || '—'}`;
}

function formatPlanPrice(plan, cycle) {
  if (cycle === 'yearly' && plan.priceYearly > 0) {
    return { main: `$${plan.priceYearly}`, suffix: '/yr' };
  }
  if (plan.priceMonthly <= 0) {
    return { main: 'Free', suffix: '' };
  }
  return { main: `$${plan.priceMonthly}`, suffix: '/mo' };
}

const SocialIcon = ({ name, href, icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="landing-social-link"
    aria-label={name}
  >
    {icon === 'facebook' && (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    )}
    {icon === 'twitter' && (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    )}
    {icon === 'linkedin' && (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
    )}
    {icon === 'instagram' && (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
    )}
  </a>
);

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sectionRefs = useRef([]);
  const [plansByRole, setPlansByRole] = useState(() => ({ ...EMPTY_PLANS }));
  const [plansLoadError, setPlansLoadError] = useState(false);
  const [planAudience, setPlanAudience] = useState('lab');
  const [planBilling, setPlanBilling] = useState('monthly');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observers = sectionRefs.current
      .filter(Boolean)
      .map((el) => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) entry.target.classList.add('landing-visible');
            });
          },
          { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
        );
        observer.observe(el);
        return observer;
      });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setPlansLoadError(false);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/subscription-plans`, { signal: ac.signal });
        if (!res.ok) throw new Error('plans');
        const data = await res.json();
        const br = data.byRole;
        if (br) {
          setPlansByRole({
            lab: br.lab || [],
            supplier: br.supplier || [],
            phlebotomist: br.phlebotomist || []
          });
        }
      } catch {
        if (!ac.signal.aborted) {
          setPlansByRole({ ...EMPTY_PLANS });
          setPlansLoadError(true);
        }
      }
    })();
    return () => ac.abort();
  }, []);

  const navLinks = [
    { to: '#home', label: 'Home' },
    { to: '#about', label: 'About Us' },
    { to: '#features', label: 'Features' },
    { to: '#subscriptions', label: 'Subscriptions' },
    { to: '#how-it-works', label: 'How It Works' },
    { to: '#contact', label: 'Contact' }
  ];

  const planRoleTabs = [
    { id: 'lab', label: 'Labs' },
    { id: 'supplier', label: 'Suppliers' },
    { id: 'phlebotomist', label: 'Phlebotomists' }
  ];

  const visiblePlans = plansByRole[planAudience] || [];

  const FeatureIcon = ({ name }) => {
    const icons = {
      network: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ),
      marketplace: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      ),
      reports: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
        </svg>
      ),
      team: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      messaging: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
      analytics: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      )
    };
    return icons[name] || null;
  };

  const features = [
    {
      icon: 'network',
      title: 'Lab & Phlebotomy Network',
      description: 'Connect labs, phlebotomists, and clinics in one platform. Post jobs, submit bids, and manage pickups seamlessly.'
    },
    {
      icon: 'marketplace',
      title: 'Reagent Marketplace',
      description: 'Labs post reagent requests; suppliers submit offers. Streamline procurement with built-in messaging and tracking.'
    },
    {
      icon: 'reports',
      title: 'Reports',
      description: 'Upload and share reports with clinics. Track earnings, wallets, and payouts—all in one place.'
    },
    {
      icon: 'team',
      title: 'Team & Task Management',
      description: 'Lab employee CRM: assign tasks, set permissions, and keep your team aligned with your lab\'s workflow.'
    },
    {
      icon: 'messaging',
      title: 'Secure Messaging',
      description: 'Communicate with labs, clinics, and suppliers in one place. Threaded conversations and file sharing built in.'
    },
    {
      icon: 'analytics',
      title: 'Analytics & Insights',
      description: 'Track performance, trends, and key metrics. View dashboards for jobs, revenue, and operational efficiency.'
    }
  ];

  const steps = [
    { step: '1', title: 'Sign Up', text: 'Register as a Lab, Clinic, Phlebotomist, or Supplier.' },
    { step: '2', title: 'Connect', text: 'Post jobs, submit bids, or request pickups and reagents.' },
    { step: '3', title: 'Manage', text: 'Use the dashboard to track tasks, messages, and payments.' }
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className={`landing-header ${scrolled ? 'landing-header--scrolled' : ''}`}>
        <div className="landing-header-inner">
          <Link to="/" className="landing-logo">
            <img src="/logo-labster.jpeg" alt="Labster" className="landing-logo-img" />
            <span className="landing-logo-text">LABSTER</span>
          </Link>
          <nav className="landing-nav">
            {navLinks.map(({ to, label }) => (
              <a key={to} href={to} className="landing-nav-link" onClick={() => setMobileMenuOpen(false)}>
                {label}
              </a>
            ))}
          </nav>
          <div className="landing-header-actions">
            {isAuthenticated ? (
              <Link to="/dashboard" className="landing-btn landing-btn-primary landing-btn-dashboard">Back to Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="landing-btn landing-btn-ghost">Login</Link>
                <Link to="/register" className="landing-btn landing-btn-primary">Sign Up</Link>
              </>
            )}
          </div>
          <button
            type="button"
            className="landing-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="landing-mobile-menu">
            {navLinks.map(({ to, label }) => (
              <a key={to} href={to} className="landing-mobile-link" onClick={() => setMobileMenuOpen(false)}>{label}</a>
            ))}
            {isAuthenticated ? (
              <Link to="/dashboard" className="landing-mobile-link landing-mobile-cta landing-btn-dashboard-mobile" onClick={() => setMobileMenuOpen(false)}>Back to Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="landing-mobile-link" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                <Link to="/register" className="landing-mobile-link landing-mobile-cta" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
              </>
            )}
          </div>
        )}
      </header>

      {/* Hero – background image (landing8) only in this section */}
      <section id="home" className="landing-hero">
        <div
          className="landing-hero-bg-image"
          aria-hidden="true"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL || ''}/landing8.jpg)`
          }}
        />
        <div className="landing-hero-bg" />
        <div className="landing-container">
          <div className="landing-hero-content">
            <p className="landing-hero-badge">Lab Network Platform</p>
            <h1 className="landing-hero-title">
              <span className="landing-hero-title-line">Connect Labs.</span>
              <span className="landing-hero-title-line">Clinics.</span>
              <span className="landing-hero-title-line landing-hero-title-accent">Phlebotomists.</span>
            </h1>
            <div className="landing-hero-divider" aria-hidden="true" />
            <p className="landing-hero-subtitle">
              One platform for lab jobs, reagent sourcing, pickup requests, and reporting. Join the network that powers modern diagnostics.
            </p>
            <div className="landing-hero-ctas">
              {isAuthenticated ? (
                <Link to="/dashboard" className="landing-btn landing-btn-primary landing-btn-lg landing-btn-dashboard">Back to Dashboard</Link>
              ) : (
                <>
                  <Link to="/register" className="landing-btn landing-btn-primary landing-btn-lg">Subscribe</Link>
                  <a href="#subscriptions" className="landing-btn landing-btn-outline landing-btn-lg">
                    View plans
                  </a>
                </>
              )}
            </div>
            {!isAuthenticated && <p className="landing-hero-trust">Trusted by 500+ labs and clinics nationwide</p>}
          </div>
        </div>
      </section>

      {/* About */}
      <section
        id="about"
        className="landing-section landing-about landing-animate"
        ref={(el) => {
          sectionRefs.current[0] = el;
        }}
      >
        <div className="landing-container">
          <div className="landing-section-header">
            <span className="landing-section-label">Overview</span>
            <h2 className="landing-section-title">About Labster</h2>
            <p className="landing-section-subtitle">
              Labster brings labs, clinics, phlebotomists, and suppliers together so that specimen collection, reagent ordering, and reporting happen in one place.
            </p>
          </div>
          <div className="landing-about-content">
            <p>We built Labster to simplify how labs coordinate with phlebotomists and clinics—post jobs, accept bids, manage pickups, and share reports without switching tools.</p>
            <p>Suppliers can respond to reagent requests and close deals through the same platform. Everyone stays in sync, from request to delivery.</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="landing-section landing-features landing-animate" ref={(el) => (sectionRefs.current[1] = el)}>
        <div className="landing-container">
          <div className="landing-section-header">
            <span className="landing-section-label">Platform</span>
            <h2 className="landing-section-title">Features</h2>
            <p className="landing-section-subtitle">Everything you need to run and grow your lab operations.</p>
          </div>
          <div className="landing-features-grid">
            {features.map((f, i) => (
              <div key={i} className="landing-feature-card">
                <div className="landing-feature-icon"><FeatureIcon name={f.icon} /></div>
                <h3 className="landing-feature-title">{f.title}</h3>
                <p className="landing-feature-desc">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscriptions / pricing */}
      <section
        id="subscriptions"
        className="landing-section landing-plans landing-animate"
        ref={(el) => {
          sectionRefs.current[2] = el;
        }}
      >
        <div className="landing-container">
          <div className="landing-section-header">
            <span className="landing-section-label">Pricing</span>
            <h2 className="landing-section-title">Subscriptions</h2>
            <p className="landing-section-subtitle">
              Plans, free-trial length, and monthly vs yearly pricing come from your administrator (aligned with Stripe). Pick a role and payment period—if nothing appears yet, plans are still being configured.
            </p>
          </div>

          <div className="landing-plans-toolbar">
            <div className="landing-plans-role-tabs" role="tablist" aria-label="Who is subscribing">
              {planRoleTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={planAudience === t.id}
                  className={`landing-plans-role-tab ${planAudience === t.id ? 'landing-plans-role-tab--active' : ''}`}
                  onClick={() => setPlanAudience(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="landing-plans-billing" role="group" aria-label="Payment period">
              <button
                type="button"
                className={`landing-plans-billing-opt ${planBilling === 'monthly' ? 'landing-plans-billing-opt--active' : ''}`}
                onClick={() => setPlanBilling('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`landing-plans-billing-opt ${planBilling === 'yearly' ? 'landing-plans-billing-opt--active' : ''}`}
                onClick={() => setPlanBilling('yearly')}
              >
                Yearly
                <span className="landing-plans-save-pill">Save</span>
              </button>
            </div>
          </div>

          <div className="landing-plans-grid">
            {visiblePlans.length === 0 && (
              <div className="landing-plan-card landing-plan-card--empty" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 24px' }}>
                <p style={{ margin: '0 0 12px 0', color: 'var(--text-primary)', fontWeight: 600 }}>
                  {plansLoadError ? 'Could not load pricing from the server.' : 'No plans published for this role yet.'}
                </p>
                <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-secondary)', maxWidth: '520px', marginLeft: 'auto', marginRight: 'auto' }}>
                  An admin adds subscription tiers in the dashboard (with Stripe product/price IDs). They appear here for Labs, Suppliers, and Phlebotomists with monthly and yearly options and any configured free trial.
                </p>
                {!isAuthenticated && (
                  <Link to="/register" className="landing-btn landing-btn-primary landing-btn-lg landing-plan-cta" style={{ marginTop: '20px' }}>
                    Sign up
                  </Link>
                )}
              </div>
            )}
            {visiblePlans.map((plan, i) => {
              const featured = plan.type === 'pro';
              const { main, suffix } = formatPlanPrice(plan, planBilling);
              const yearlyNote =
                planBilling === 'yearly' && plan.priceYearly > 0 && plan.priceMonthly > 0
                  ? `~$${Math.round(plan.priceYearly / 12)}/mo billed annually`
                  : null;
              const trialDays = Number(plan.trialDays) || 0;
              return (
                <article
                  key={plan._id || String(i)}
                  className={`landing-plan-card ${featured ? 'landing-plan-card--featured' : ''}`}
                >
                  {featured ? (
                    <span className="landing-plan-ribbon">Popular</span>
                  ) : null}
                  <h3 className="landing-plan-name">{plan.name}</h3>
                  <p className="landing-plan-price">
                    <span className="landing-plan-price-main">{main}</span>
                    {suffix ? <span className="landing-plan-price-suffix">{suffix}</span> : null}
                  </p>
                  {trialDays > 0 ? (
                    <p className="landing-plan-discount" style={{ marginTop: '4px' }}>
                      {trialDays}-day free trial
                    </p>
                  ) : null}
                  {yearlyNote ? <p className="landing-plan-yearly-note">{yearlyNote}</p> : null}
                  {planBilling === 'yearly' && plan.yearlyDiscountPercent > 0 && plan.priceYearly > 0 ? (
                    <p className="landing-plan-discount">{plan.yearlyDiscountPercent}% off vs monthly</p>
                  ) : null}
                  <p className="landing-plan-summary">{planSummaryLine(plan, planAudience)}</p>
                  <ul className="landing-plan-features">
                    {(plan.features || []).slice(0, 8).map((f, fi) => (
                      <li key={fi}>{f}</li>
                    ))}
                  </ul>
                  <Link
                    to={`/register?role=${planAudience}`}
                    className={`landing-btn landing-btn-lg ${featured ? 'landing-btn-primary' : 'landing-btn-outline'} landing-plan-cta`}
                  >
                    Get started
                  </Link>
                </article>
              );
            })}
          </div>

          <p className="landing-plans-footnote">
            Clinics typically join through a lab invitation. Need a custom enterprise plan?{' '}
            <a href="#contact" className="landing-plans-footnote-link">
              Contact us
            </a>
            .
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="landing-section landing-steps landing-animate" ref={(el) => (sectionRefs.current[3] = el)}>
        <div className="landing-container">
          <div className="landing-section-header">
            <span className="landing-section-label">Getting Started</span>
            <h2 className="landing-section-title">How It Works</h2>
            <p className="landing-section-subtitle">Get started in three simple steps.</p>
          </div>
          <div className="landing-steps-grid">
            {steps.map((s, i) => (
              <div key={i} className="landing-step-card">
                <div className="landing-step-num">{s.step}</div>
                <h3 className="landing-step-title">{s.title}</h3>
                <p className="landing-step-text">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="landing-section landing-cta landing-animate" ref={(el) => (sectionRefs.current[4] = el)}>
        <div className="landing-container">
          <div className="landing-cta-box">
            <h2 className="landing-cta-title">Ready to get started?</h2>
            <p className="landing-cta-text">Join the Labster network and streamline your lab operations.</p>
            <div className="landing-cta-buttons">
              {isAuthenticated ? (
                <Link to="/dashboard" className="landing-btn landing-btn-primary landing-btn-lg landing-btn-dashboard">Back to Dashboard</Link>
              ) : (
                <>
                  <Link to="/register" className="landing-btn landing-btn-primary landing-btn-lg">Subscribe</Link>
                  <Link to="/login" className="landing-btn landing-btn-outline landing-btn-lg">Login</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="landing-footer-inner">
            <div className="landing-footer-brand">
              <img src="/logo-labster.jpeg" alt="Labster" className="landing-footer-logo" />
              <span>Labster</span>
            </div>
            <div className="landing-footer-links">
              <a href="#home">Home</a>
              <a href="#about">About</a>
              <a href="#features">Features</a>
              <a href="#subscriptions">Subscriptions</a>
              <a href="#how-it-works">How It Works</a>
              {isAuthenticated ? (
                <Link to="/dashboard">Dashboard</Link>
              ) : (
                <>
                  <Link to="/login">Login</Link>
                  <Link to="/register">Subscribe</Link>
                </>
              )}
            </div>
            <div className="landing-footer-social">
              {socialLinks.map((s) => (
                <SocialIcon key={s.icon} name={s.name} href={s.href} icon={s.icon} />
              ))}
            </div>
          </div>
          <p className="landing-footer-copy">© {new Date().getFullYear()} Labster. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
